import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Upload, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = () => ({
  name: '',
  category: '',
  description: '',
  price: '',
  originalPrice: '',
  stock: 0,
  fabric: '',
  style: '',
  color: '',
  work: '',
  visible: true,
  images: [],
});

export const NewProductDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(initial());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleImagePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const newUrls = [];
      for (const file of files) {
        const base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        // Use category image-upload endpoint for staging since the product
        // doesn't exist yet. It returns a Cloudinary secure_url we'll attach.
        const res = await axios.post(`${API}/admin/upload-image`, { image: base64 }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.image_url) newUrls.push(res.data.image_url);
      }
      set('images', [...form.images, ...newUrls]);
      toast.success(`${newUrls.length} image${newUrls.length > 1 ? 's' : ''} uploaded`);
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    set('images', form.images.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.category.trim() || !form.price) {
      toast.error('Name, category and price are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        price: parseInt(form.price, 10) || 0,
        originalPrice: form.originalPrice ? parseInt(form.originalPrice, 10) : null,
        stock: parseInt(form.stock, 10) || 0,
        fabric: form.fabric.trim(),
        style: form.style.trim(),
        color: form.color.trim(),
        work: form.work.trim(),
        visible: !!form.visible,
        images: form.images,
      };
      const res = await axios.post(`${API}/admin/products`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product created');
      onCreated?.(res.data.product);
      setForm(initial());
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl bg-[#FAF9F7] max-h-[90vh] overflow-y-auto" data-testid="new-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-seasons text-2xl text-[#2C2C2C]">Add New Product</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 py-2">
          <Field label="Name *">
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Royal Banarasi Silk Saree" data-testid="np-name" />
          </Field>
          <Field label="Category *">
            <Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Silk Sarees" data-testid="np-category" />
          </Field>
          <Field label="Price (INR) *">
            <Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="2999" data-testid="np-price" />
          </Field>
          <Field label="Original Price (optional)">
            <Input type="number" value={form.originalPrice} onChange={(e) => set('originalPrice', e.target.value)} placeholder="3999" data-testid="np-original-price" />
          </Field>
          <Field label="Stock">
            <Input type="number" value={form.stock} onChange={(e) => set('stock', e.target.value)} data-testid="np-stock" />
          </Field>
          <Field label="Fabric">
            <Input value={form.fabric} onChange={(e) => set('fabric', e.target.value)} placeholder="e.g. Pure Silk" data-testid="np-fabric" />
          </Field>
          <Field label="Style">
            <Input value={form.style} onChange={(e) => set('style', e.target.value)} placeholder="e.g. Traditional" />
          </Field>
          <Field label="Color">
            <Input value={form.color} onChange={(e) => set('color', e.target.value)} placeholder="e.g. Maroon" />
          </Field>
          <Field label="Work">
            <Input value={form.work} onChange={(e) => set('work', e.target.value)} placeholder="e.g. Zari Embroidery" />
          </Field>
          <Field label="Visibility">
            <label className="flex items-center gap-2 mt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) => set('visible', e.target.checked)}
                className="h-4 w-4 accent-[#C4B5A0]"
                data-testid="np-visible"
              />
              <span className="text-sm font-montserrat text-[#2C2C2C]">
                {form.visible ? 'Visible on website' : 'Hidden from website'}
              </span>
            </label>
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={3}
            className="w-full bg-white border border-[#E8E2D5] rounded-md p-2 text-sm font-montserrat text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0]"
            placeholder="Detailed product description shown on the product page..."
            data-testid="np-description"
          />
        </Field>

        <Field label="Images">
          <div className="flex items-center gap-2 flex-wrap" data-testid="np-images-row">
            {form.images.map((img, idx) => (
              <div key={idx} className="relative group">
                <img src={img} alt={`upload-${idx}`} className="h-16 w-16 object-cover rounded border border-[#E8E2D5]" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                  data-testid={`np-remove-image-${idx}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-[#C4B5A0] text-[#6B4E37] rounded-md px-3 py-2 hover:bg-[#F5F3F0]">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="text-xs font-montserrat">{uploading ? 'Uploading…' : 'Add images'}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleImagePick} disabled={uploading} data-testid="np-image-input" />
            </label>
          </div>
        </Field>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { setForm(initial()); onClose?.(); }} className="border-[#8B7D6B] text-[#8B7D6B]">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || uploading}
            className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white"
            data-testid="np-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Product
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-1">{label}</label>
    {children}
  </div>
);
